import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Globe, Plus, RefreshCw, Trash2, Loader2, ChevronDown } from "lucide-react";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { getTrainingSites, addTrainingSitesBulk, refreshTrainingSite, deleteTrainingSite } from "@/lib/dentiumApi";
import { errText } from "@/components/training/trainingUtils";
import { ConfirmDelete } from "@/components/training/ConfirmDelete";

const pagesLabel = (n) => `${n} ${n === 1 ? "página" : "páginas"}`;

const when = (iso) => (iso ? new Date(iso).toLocaleString("es-ES", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" }) : "—");

const SiteRow = ({ site }) => {
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const [confirm, setConfirm] = useState(false);
  const refresh = () => { qc.invalidateQueries({ queryKey: ["training-sites"] }); qc.invalidateQueries({ queryKey: ["kb-entries"] }); };
  const reload = useMutation({
    mutationFn: () => refreshTrainingSite(site.id),
    onSuccess: (d) => { refresh(); d.pages.length ? toast.success("Web actualizada", { description: `${pagesLabel(d.pages.length)} leídas.` }) : toast.warning("No se pudo leer la web"); },
    onError: (e) => toast.error(errText(e, "No se pudo actualizar")),
  });
  const remove = useMutation({ mutationFn: () => deleteTrainingSite(site.id), onSuccess: () => { refresh(); toast.success("Web eliminada"); }, onError: (e) => toast.error(errText(e, "No se pudo eliminar la web")) });
  return (
    <div data-testid={`training-site-${site.id}`} className="px-4 py-3">
      <div className="flex items-center gap-2">
        <button onClick={() => setOpen((v) => !v)} className="min-w-0 flex-1 text-left">
          <div className="text-sm font-medium text-[#1A1A1A] truncate" title={site.url}>{site.url.replace(/^https?:\/\/(www\.)?/, "")}</div>
          <div className="text-[11px] text-[#8A7E70]" data-testid="training-site-info">
            {site.status === "error" ? <span className="text-amber-700">No se pudo leer</span>
              : site.status === "pendiente" || site.status === "leyendo" ? <span className="text-sky-700 inline-flex items-center gap-1"><Loader2 className="h-3 w-3 animate-spin" /> Leyendo…</span>
              : `${pagesLabel(site.pages.length)} · actualizado ${when(site.last_crawled_at)}`}
            <ChevronDown className={`inline h-3 w-3 ml-1 transition-transform ${open ? "rotate-180" : ""}`} />
          </div>
        </button>
        <button data-testid="training-site-refresh" onClick={() => reload.mutate()} disabled={reload.isPending || ["pendiente", "leyendo"].includes(site.status)} title="Actualizar ahora"
          className="h-8 px-2.5 rounded-full text-xs font-medium inline-flex items-center gap-1 text-[#1A1A1A] hover:bg-[#F1EBE1] disabled:opacity-50">
          <RefreshCw className={`h-3.5 w-3.5 ${reload.isPending ? "animate-spin" : ""}`} /> <span className="hidden sm:inline">{reload.isPending ? "Leyendo…" : "Actualizar ahora"}</span>
        </button>
        <button data-testid="training-site-delete" onClick={() => setConfirm(true)} title="Quitar" className="h-8 w-8 rounded-full flex items-center justify-center text-[#A79E90] hover:bg-red-50 hover:text-red-600"><Trash2 className="h-4 w-4" /></button>
      </div>
      <ConfirmDelete open={confirm} onOpenChange={setConfirm} testid="training-site-delete-dialog" title="¿Quitar esta web?"
        description={`El asistente olvidará las ${pagesLabel(site.pages.length)} leídas de ${site.url}.`} onConfirm={() => remove.mutate()} />
      {open && (
        <ul className="mt-2 space-y-1 pl-1" data-testid="training-site-pages">
          {site.pages.map((p) => (
            <li key={p.url} className="text-[11px] text-[#6B6157] truncate">· {p.title} <span className="text-[#A79E90]">({p.chars.toLocaleString("es-ES")} car.)</span></li>
          ))}
        </ul>
      )}
    </div>
  );
};

export const SitesCard = () => {
  const qc = useQueryClient();
  const [url, setUrl] = useState("");
  const [follow, setFollow] = useState(true);
  const { data: sites = [] } = useQuery({
    queryKey: ["training-sites"], queryFn: getTrainingSites,
    refetchInterval: (q) => ((q.state.data || []).some((x) => x.status === "pendiente" || x.status === "leyendo") ? 3000 : false),
  });
  const count = url.split(/[\s,;]+/).filter(Boolean).length;
  const add = useMutation({
    mutationFn: () => addTrainingSitesBulk({ text: url, follow_links: follow }),
    onSuccess: (d) => {
      setUrl("");
      qc.invalidateQueries({ queryKey: ["training-sites"] });
      qc.invalidateQueries({ queryKey: ["kb-entries"] });
      if (d.added.length) toast.success(`${d.added.length} ${d.added.length === 1 ? "web añadida" : "webs añadidas"}`, { description: "Se están leyendo; verás el progreso en la lista." });
      if (d.skipped.length) toast.info(d.skipped.length === 1 ? "1 ya estaba añadida" : `${d.skipped.length} ya estaban añadidas`, { description: d.skipped.join(", ") });
      if (d.invalid.length) toast.warning("Algunas direcciones no son válidas", { description: d.invalid.join(", ") });
    },
    onError: (e) => toast.error(errText(e, "No se pudieron añadir las webs")),
  });
  return (
    <div className="rounded-2xl border border-[#E7E2DA] bg-white overflow-hidden" data-testid="training-sites-card">
      <div className="px-5 pt-5">
        <div className="flex items-center gap-2 mb-1">
          <span className="h-9 w-9 rounded-xl bg-[#F1EBE1] flex items-center justify-center"><Globe className="h-4.5 w-4.5 text-[#1A1A1A]" /></span>
          <h2 className="text-lg font-medium text-[#1A1A1A]">Aprender de páginas web</h2>
        </div>
        <p className="text-sm text-[#8A7E70] mb-4 ml-11">Pega una o varias direcciones (una por línea). Se vuelven a leer solas cada día.</p>
        <form onSubmit={(e) => { e.preventDefault(); if (url.trim()) add.mutate(); }}>
          <Textarea data-testid="training-site-url-input" value={url} onChange={(e) => setUrl(e.target.value)} rows={3} className="resize-none text-sm"
            placeholder={"https://tuclinica.com\nhttps://tuclinica.com/tratamientos\nhttps://tuclinica.com/precios"} />
          <button data-testid="training-site-add-btn" type="submit" disabled={!url.trim() || add.isPending}
            className="mt-2 w-full h-11 px-4 rounded-md bg-[#1A1A1A] hover:bg-black text-white text-sm font-medium inline-flex items-center justify-center gap-1.5 disabled:opacity-50">
            {add.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />}
            {count > 1 ? `Añadir ${count} webs` : "Añadir web"}
          </button>
        </form>
        <label className="flex items-center gap-2 mt-3 mb-4 text-xs text-[#6B6157] cursor-pointer">
          <Switch data-testid="training-site-follow-switch" checked={follow} onCheckedChange={setFollow} />
          {follow ? "Leer también las páginas principales enlazadas (hasta 8)" : "Leer solo esta página"}
        </label>
      </div>
      {sites.length > 0 && <div className="border-t border-[#EDE7DE] divide-y divide-[#EDE7DE]" data-testid="training-sites-list">{sites.map((s) => <SiteRow key={s.id} site={s} />)}</div>}
    </div>
  );
};
